import {
  IPlatformService,
  Platform,
  TVKeyMap,
  DeviceInfo,
  BatteryInfo,
  ScreenOrientation,
  OrientationChangeCallback,
} from '../types';
import { BROWSER_KEYS } from '../keys';
import { generateUUID } from '../../../utils/uuid';
import { Device } from '@capacitor/device';
import { Filesystem, Directory } from '@capacitor/filesystem';
// 2026-05-30 — import estático do plugin de brilho. Antes usávamos
// `await import('@capacitor-community/screen-brightness')` com /* @vite-ignore */
// pra evitar peer dep falhando em build-time, mas isso fazia o Vite NÃO bundleear
// o módulo em `vite build` (production). Em runtime no APK release o
// import dinâmico tentava resolver via URL e falhava silenciosamente — o
// BrightnessService caía no overlay CSS sem tocar no hardware.
// Agora o plugin é peerDep @capacitor/core ^7 (compatível) e está
// instalado/cap-synced no projeto via `scripts/ensure-brightness-plugin.sh`.
import { ScreenBrightness } from '@capacitor-community/screen-brightness';
import { otaLog } from '~/services/ota/OtaDebugLogger';
import type {
  NavigatorWithBattery,
  DocumentWithVendorFullscreen,
  ElementWithVendorFullscreen,
} from '../dom-globals';

export class AndroidPlatformService implements IPlatformService {
  readonly platform: Platform = 'android';
  readonly keys: TVKeyMap = BROWSER_KEYS; // Android usually uses standard browser keys or touchscreen
  private isInitialized = false;
  private orientationCallbacks: Set<OrientationChangeCallback> = new Set();
  private fullscreenCallbacks: Set<(isFullscreen: boolean) => void> = new Set();
  private currentOrientation: ScreenOrientation = 'landscape';
  // v1.0.147 — id do poll periódico de orientação (vide setupOrientationListener).
  private orientationPollId: number | null = null;

  init(): void {
    if (this.isInitialized) return;

    console.log('[AndroidPlatformService] Initializing Capacitor/Android...');
    this.isInitialized = true;

    // Here we can initialize Capacitor plugins if needed
    // e.g., SplashScreen.hide(), App.addListener('backButton', ...)

    // Setup orientation listener
    this.setupOrientationListener();
  }

  private setupOrientationListener(): void {
    // Detect initial orientation
    this.currentOrientation = this.detectOrientation();
    console.log(
      '[AndroidPlatformService] Initial orientation:',
      this.currentOrientation,
    );

    // Registra MÚLTIPLOS listeners simultaneamente — defesa contra WebView
    // antigo do Tab A7 (Android 12 stock WebView) onde apenas UM dos eventos
    // dispara em cada situação:
    // - `screen.orientation 'change'`: dispara em rotações "claras"
    // - `resize`: dispara via onConfigurationChanged + WebView.requestLayout()
    // - `orientationchange` (legacy DOM): dispara em alguns WebViews onde os
    //   acima silenciam, especialmente durante cold-start + rotação rápida
    // - `visibilitychange`: re-check quando app volta a foreground
    //
    // O {@code handleOrientationChange} tem dedup interno (só notifica se
    // {@code currentOrientation} mudou), então duplicação é safe.
    if (screen.orientation) {
      try {
        screen.orientation.addEventListener(
          'change',
          this.handleOrientationChangeWithDelay,
        );
      } catch (e) {
        console.warn(
          '[AndroidPlatformService] screen.orientation listener falhou:',
          e,
        );
      }
    }
    window.addEventListener('resize', this.handleResize);
    // v1.0.190 (Renato 2026-06-14): legacy orientationchange event.
    // Dispara em WebViews antigos quando screen.orientation 'change' silencia.
    // Especialmente útil em rotação RÁPIDA durante cold-start (primeiros 5s
    // do boot) — Tab A7 WebView demora alguns frames pra refletir mudança.
    window.addEventListener(
      'orientationchange',
      this.handleOrientationChangeWithDelay,
    );
    // v1.0.190: re-check ao voltar a foreground (app retorna após split-screen,
    // recents, ou desbloqueio em outra orientação).
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.handleOrientationChangeWithDelay();
      }
    });
    // Orientação física nativa (MainActivity.onConfigurationChanged) — fonte
    // autoritativa quando WebView inner* fica stale em kiosk ON (Tab A7).
    window.addEventListener(
      'efflux-native-orientation',
      this.handleNativeOrientationEvent,
    );

    // v1.0.146 — re-checagens no startup (Renato 2026-06-05). A 1ª leitura
    // pós-boot/provisioning pode pegar a orientação ERRADA: o WebView/Activity
    // ainda não assentou (manifest=fullSensor, e durante o setup o tablet pode
    // ter oscilado). Como o device fica PARADO depois (sem evento de rotação),
    // NADA corrigiria até um reboot — sintoma: instalou em portrait mas o Efflux
    // subiu em landscape, só consertou reiniciando.
    //
    // v1.0.190 (Renato 2026-06-14): adicionados re-checks AGRESSIVOS em
    // 100ms/300ms/700ms ANTES dos checks de 2s/5s/10s. Cobre o cold-start
    // do Tab A7: WebView precisa de ~200-500ms pra refletir window.inner*
    // corretamente. Antes desse fix, usuário via tela "renderizar em
    // portrait" e só corrigir após 2s (já tinha visto a tela errada).
    //
    // requestAnimationFrame: garante que rolou ao menos 1 frame de layout
    // (window.inner* devem estar populados após primeiro paint).
    //
    // handleOrientationChange tem dedup interno — só dispara updateOrientation
    // + forceResync se a orientação assentada for diferente da inicial.
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => this.handleOrientationChange());
    }
    [1000, 3000, 5000, 10000].forEach((delayMs) => {
      setTimeout(() => this.handleOrientationChange(), delayMs);
    });

    // v1.0.190 (Renato 2026-06-15): poll AGRESSIVO no boot pra cobrir
    // rotação física DURANTE inicialização. Cenário descoberto em campo:
    //   1. Tablet em landscape, user reinicia app
    //   2. Durante o boot (JS ainda inicializando), user gira pra portrait
    //   3. JS perde os eventos screen.orientation/resize/orientationchange
    //      (listeners ainda não registrados ou WebView ainda não relayout)
    //   4. Quando o poll normal de 3s roda, currentOrientation já foi setado
    //      no init com a orientação ERRADA (landscape do momento do init)
    //   5. detectOrientation pode retornar landscape (WebView cache stale)
    //      ou portrait (atualizado) — depende da timing
    //
    // Poll agressivo a cada 500ms pelos primeiros 15s GARANTE que qualquer
    // estabilização do WebView no boot seja detectada rápido. Após 15s,
    // o poll de 3s normal toma conta. handleOrientationChange tem dedup —
    // chamadas redundantes são no-op.
    const aggressiveStartMs = Date.now();
    const aggressiveBootPoll = window.setInterval(() => {
      this.handleOrientationChange();
      if (Date.now() - aggressiveStartMs > 15000) {
        window.clearInterval(aggressiveBootPoll);
      }
    }, 500);

    // v1.0.147 — POLL periódico de orientação (Renato 2026-06-05). As
    // re-leituras acima só cobrem os 10s iniciais; mas no campo o operador
    // PROVISIONA segurando o tablet (landscape) e MONTA em portrait DEPOIS —
    // minutos depois. No Tab A7 o evento screen.orientation 'change'/'resize'
    // às vezes SILENCIA nessa rotação tardia (a TELA gira, mas a detecção/botão
    // ficam defasados → "reconhece horizontal"). O poll garante que QUALQUER
    // mudança (mesmo tardia) seja pega: re-lê e, via dedup do
    // handleOrientationChange, só age (updateOrientation + forceResync) se mudou.
    //
    // v1.0.190 (Renato 2026-06-14): intervalo reduzido de 7s → 3s. Cobre
    // melhor o cenário de rotação rápida no boot — o user gira o tablet
    // nos primeiros 5s e o app demora pra recuperar a orientação correta.
    // Custo: 1 leitura de window.inner* a cada 3s — desprezível. 100% interno.
    this.orientationPollId = window.setInterval(() => {
      this.handleOrientationChange();
    }, 3000);
  }

  private handleOrientationChange = (): void => {
    const newOrientation = this.detectOrientation();
    if (newOrientation !== this.currentOrientation) {
      console.log(
        '[AndroidPlatformService] Orientation changed:',
        this.currentOrientation,
        '->',
        newOrientation,
      );
      // v1.0.190 — log nativo só quando MUDA (evita spam: o poll roda a cada 3s).
      otaLog.info('Orientation changed (notifying)', {
        from: this.currentOrientation,
        to: newOrientation,
        callbacks: this.orientationCallbacks.size,
      });
      this.currentOrientation = newOrientation;
      this.notifyOrientationChange(newOrientation);
    }
  };

  /**
   * v1.0.190 (Renato 2026-06-14): handler com re-checks em cascata.
   *
   * Após receber qualquer evento de orientação (resize, orientationchange,
   * screen.orientation 'change', visibilitychange), agenda 3 re-checks
   * em 150/500/1500ms ALÉM da chamada imediata.
   *
   * Motivo: WebView Android tem "transient state" durante rotação onde
   * window.inner* e screen.orientation.type podem estar dessincronizados
   * por alguns frames. Especialmente em cold-start + rotação rápida
   * (primeiros 5s do boot), uma única leitura pode pegar o estado
   * intermediário e ficar grudada.
   *
   * Os re-checks em cascata garantem que pelo menos uma leitura pegue
   * o estado estável. handleOrientationChange tem dedup interno, então
   * leituras redundantes são no-op.
   */
  private handleOrientationChangeWithDelay = (): void => {
    this.handleOrientationChange();
    // v1.0.190 (Renato 2026-06-14): cadência 1s/3s/5s/10s pós-evento.
    // Mais conservador que 150/500/1500ms — em 10s o WebView estabilizou.
    // Com dedup interno do handleOrientationChange, leituras redundantes
    // são no-op. Custo desprezível.
    [1000, 3000, 5000, 10000].forEach((delayMs) => {
      setTimeout(() => this.handleOrientationChange(), delayMs);
    });
  };

  private handleResize = (): void => {
    this.handleOrientationChangeWithDelay();
  };

  /** MainActivity disparou orientação nativa (Configuration) — prioridade máxima. */
  private handleNativeOrientationEvent = (): void => {
    otaLog.info('Native orientation event received', {
      native: (window as Window & { __effluxNativeOrientation?: string })
        .__effluxNativeOrientation,
    });
    this.handleOrientationChangeWithDelay();
  };

  private detectOrientation(): ScreenOrientation {
    // v1.0.199 (Renato 2026-06-23): Configuration nativa do Android vence
    // window.inner* quando kiosk ON mantém viewport stale após rotação física.
    const native = (
      window as Window & { __effluxNativeOrientation?: ScreenOrientation }
    ).__effluxNativeOrientation;
    if (native === 'portrait' || native === 'landscape') {
      const iw = window.innerWidth || 0;
      const ih = window.innerHeight || 0;
      if (iw > 50 && ih > 50) {
        const viewport = iw > ih ? 'landscape' : 'portrait';
        if (viewport !== native) {
          otaLog.info('Orientation: native vence viewport stale', {
            native,
            viewport,
            iw,
            ih,
          });
        }
      }
      return native;
    }

    // ATENÇÃO: ordem importa. No Capacitor/WebView Android,
    // screen.orientation.type pode reportar "landscape-primary" mesmo
    // quando o tablet está fisicamente em portrait (observado no
    // samsung SM-T505: screen.width=800, screen.height=1334 → portrait,
    // mas screen.orientation.type='landscape-primary'). Por isso a
    // comparação de viewport (window.inner*) tem prioridade — reflete
    // o que o usuário está realmente vendo.
    const iw = window.innerWidth || 0;
    const ih = window.innerHeight || 0;
    // v1.0.190 (Renato 2026-06-14): threshold mínimo 50px pra evitar
    // leitura precoce no cold-start. WebView Android no boot pode
    // reportar window.inner* zerados ou minúsculos (não populados),
    // caindo no fallback screen.width/height que reflete a orientação
    // PHYSICAL NATIVE do device. Tab A7 SM-T505 native = 1200x2000
    // (portrait). Resultado: app subia em portrait scale mesmo com
    // tablet fisicamente em landscape até o usuário girar pra disparar
    // resize event. Threshold 50px é seguro (qualquer viewport real >>50).
    if (iw > 50 && ih > 50) {
      return iw > ih ? 'landscape' : 'portrait';
    }
    // Fallback: screen.availWidth/availHeight (viewport útil sem
    // status/nav bars — reflete orientação atual, não native).
    // Mais confiável que screen.width/height no cold-start.
    const aw = screen.availWidth || 0;
    const ah = screen.availHeight || 0;
    if (aw > 0 && ah > 0) {
      return aw > ah ? 'landscape' : 'portrait';
    }
    // Penúltimo recurso: screen.width/height — pode refletir orientação
    // physical native (Tab A7 native = portrait → 'portrait' mesmo
    // quando tablet está em landscape).
    const sw = screen.width || 0;
    const sh = screen.height || 0;
    if (sw > 0 && sh > 0) {
      return sw > sh ? 'landscape' : 'portrait';
    }
    // Último recurso: API declarativa (pode mentir, ver comentário acima)
    if (screen.orientation?.type) {
      return screen.orientation.type.includes('portrait')
        ? 'portrait'
        : 'landscape';
    }
    return 'landscape';
  }

  private notifyOrientationChange(orientation: ScreenOrientation): void {
    this.orientationCallbacks.forEach((callback) => {
      try {
        callback(orientation);
      } catch (e) {
        console.error(
          '[AndroidPlatformService] Error in orientation callback:',
          e,
        );
      }
    });
  }

  exit(): void {
    console.log('[AndroidPlatformService] Exiting app (Capacitor)...');
    try {
      // Capacitor é injetado dinamicamente na webview Android — sem types disponíveis
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Capacitor = (window as any).Capacitor;
      if (Capacitor && Capacitor.Plugins.App) {
        Capacitor.Plugins.App.exitApp();
      }
    } catch (e) {
      console.error('Failed to exit app', e);
    }
  }

  /**
   * Restart remoto (PLAYER_RESTART_PLAN).
   * - 'refresh': window.location.reload() recarrega o WebView
   * - 'reboot_device': delega pro KioskMode plugin nativo (DPM.reboot).
   *   Só funciona se backend == device_owner; senão throw 'unsupported'.
   */
  async restart(mode: 'refresh' | 'reboot_device'): Promise<void> {
    if (mode === 'refresh') {
      console.log(
        '[AndroidPlatformService] Restart refresh — recarregando WebView',
      );
      window.location.reload();
      return;
    }
    // reboot_device
    const { tryNativeRebootDevice } = await import('../kioskMode');
    const result = await tryNativeRebootDevice();
    if (!result) {
      throw new Error('unsupported: KioskMode plugin indisponível');
    }
    if (!result.supported) {
      throw new Error(
        `unsupported: ${result.reason ?? 'backend não suporta reboot_device'}`,
      );
    }
    console.log(
      '[AndroidPlatformService] Reboot device via',
      result.backend,
      '— status:',
      result.status,
    );
  }

  onHide(): void {
    console.log('[AndroidPlatformService] App backgrounded');
  }

  onResume(): void {
    console.log('[AndroidPlatformService] App resumed');
  }

  isNativeEnvironment(): boolean {
    return true; // Capacitor considered native
  }

  canExit(): boolean {
    return true;
  }

  hasColorButtons(): boolean {
    return false;
  }

  hasMediaKeys(): boolean {
    return true;
  }

  /** Não é TV: tem toque ou mouse, e não sofre o limite de 1 decoder das TVs. */
  isTV(): boolean {
    return false;
  }

  /**
   * Returns the Android device ID using Capacitor Device plugin.
   * Falls back to a generated UUID if Capacitor is not available.
   */
  async getHardwareId(): Promise<string> {
    try {
      // Capacitor é injetado dinamicamente na webview Android — sem types disponíveis
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Capacitor = (window as any).Capacitor;
      if (Capacitor?.Plugins?.Device) {
        const info = await Capacitor.Plugins.Device.getId();
        if (info?.identifier || info?.uuid) {
          const deviceId = info.identifier || info.uuid;
          const hardwareId = `ANDROID-${deviceId}`;
          console.log(
            '[AndroidPlatformService] Hardware ID obtained:',
            hardwareId.substring(0, 20) + '...',
          );
          return hardwareId;
        }
      }
    } catch (error) {
      console.warn(
        '[AndroidPlatformService] Capacitor Device plugin failed:',
        error,
      );
    }

    // Fallback to localStorage-based ID
    let devId = localStorage.getItem('dev_android_hardware_id');
    // Migration: If old DEV- ID exists, ignore/overwrite it
    if (devId && devId.startsWith('DEV-')) {
      devId = null;
    }

    if (!devId) {
      // Use ANDROID-FALLBACK prefix to be accepted by server but identified as fallback
      devId = `ANDROID-FALLBACK-${generateUUID().substring(0, 8).toUpperCase()}`;
      localStorage.setItem('dev_android_hardware_id', devId);
    }
    console.log('[AndroidPlatformService] Using fallback ID:', devId);
    return devId;
  }

  /**
   * Collects device information using Capacitor Device plugin.
   */
  /**
   * RAM livre do aparelho via `ActivityManager.MemoryInfo` (plugin nativo).
   *
   * <p>É o número que faltava no Android: até aqui só a Tizen reportava, e o
   * diagnóstico do vazamento no tablet dependia de alguém plugar o cabo e rodar
   * `dumpsys`. Na série de saúde, a queda aparece sozinha.</p>
   *
   * <p>O plugin é opcional de propósito: APK antigo (sem ele) devolve null e o
   * player segue igual, sem erro.</p>
   */
  async getSystemMemoryMb(): Promise<{
    freeMb: number;
    totalMb: number | null;
  } | null> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const plugin = (window as any).Capacitor?.Plugins?.DeviceMetrics;
      if (!plugin?.getMemoryInfo) return null;
      const info = await plugin.getMemoryInfo();
      const free = Number(info?.availMb);
      if (!Number.isFinite(free) || free <= 0) return null;
      const total = Number(info?.totalMb);
      return {
        freeMb: Math.round(free),
        totalMb: Number.isFinite(total) && total > 0 ? Math.round(total) : null,
      };
    } catch {
      return null;
    }
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    const baseInfo: DeviceInfo = {
      platform: 'android',
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      orientation: this.getOrientation(),
    };

    try {
      // Capacitor é injetado dinamicamente na webview Android — sem types disponíveis
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Capacitor = (window as any).Capacitor;
      if (Capacitor?.Plugins?.Device) {
        const info = await Capacitor.Plugins.Device.getInfo();
        baseInfo.model = info.model || 'Unknown';
        baseInfo.manufacturer = info.manufacturer || 'Android';
        baseInfo.platformVersion =
          info.osVersion || info.operatingSystem || 'Unknown';

        // Hardware identifiers em extra → backend grava em metadata JSONB.
        // ANDROID_ID (via Device.getId) é estável por app+device, sobrevive
        // a updates e reinício; muda só em factory reset. Usado pra
        // rastreio individual + troubleshooting em campo.
        const extra: Record<string, unknown> = { ...info };
        try {
          const idResult = await Capacitor.Plugins.Device.getId();
          if (idResult?.identifier || idResult?.uuid) {
            extra.androidId = idResult.identifier || idResult.uuid;
          }
        } catch (err) {
          console.warn('[AndroidPlatformService] Device.getId failed:', err);
        }
        baseInfo.extra = extra;
      }
    } catch (error) {
      console.warn(
        '[AndroidPlatformService] Error getting device info:',
        error,
      );
      baseInfo.model = 'Android Device';
      baseInfo.manufacturer = 'Android';
    }

    // Collect battery information using Capacitor Device plugin (native)
    try {
      const batteryManager = await Device.getBatteryInfo();
      if (typeof batteryManager.batteryLevel === 'number') {
        const batteryInfo: BatteryInfo = {
          level: Math.round(batteryManager.batteryLevel * 100),
          isCharging: Boolean(batteryManager.isCharging),
        };
        baseInfo.battery = batteryInfo;
        console.log(
          '[AndroidPlatformService] Battery info collected:',
          batteryInfo,
        );
      }
    } catch (error) {
      console.warn(
        '[AndroidPlatformService] Battery API not available:',
        error,
      );
      // Fallback to Battery Status API if available (webview)
      try {
        const getBattery = (navigator as NavigatorWithBattery).getBattery;
        if (getBattery) {
          const batteryManager = await getBattery.call(navigator);
          const batteryInfo: BatteryInfo = {
            level: Math.round(batteryManager.level * 100),
            isCharging: batteryManager.charging,
          };
          baseInfo.battery = batteryInfo;
          console.log(
            '[AndroidPlatformService] Battery info collected via web API:',
            batteryInfo,
          );
        }
      } catch (fallbackError) {
        console.warn(
          '[AndroidPlatformService] Battery web API not available:',
          fallbackError,
        );
      }
    }

    console.log('[AndroidPlatformService] Device info collected:', baseInfo);
    return baseInfo;
  }

  // Orientation methods

  /**
   * Android devices support orientation changes at runtime.
   */
  supportsOrientationChange(): boolean {
    return true;
  }

  /**
   * Gets current display orientation.
   *
   * v1.0.190 (Renato 2026-06-15): NÃO muta mais this.currentOrientation.
   * Antes, getOrientation atualizava silenciosamente, quebrando o invariante
   * do dedup em handleOrientationChange.
   *
   * Cenário do bug: watchdog do useOrientationSync chamava getOrientation
   * → mutava currentOrientation = portrait sem notificar callbacks → o poll
   * de 3s vinha depois, fazia handleOrientationChange → detectOrientation
   * retornava portrait === currentOrientation → dedup engolia → callbacks
   * (useOrientation React state) NUNCA disparavam → tela ficava grudada
   * em landscape mesmo o conteúdo sendo portrait.
   *
   * Agora getOrientation só DETECTA, não MUTA. Mutation de currentOrientation
   * é exclusiva de handleOrientationChange — que SEMPRE notifica via
   * notifyOrientationChange quando muda. Garante consistência.
   */
  getOrientation(): ScreenOrientation {
    return this.detectOrientation();
  }

  /**
   * Registers a callback for orientation change events.
   * Returns an unsubscribe function.
   */
  onOrientationChange(callback: OrientationChangeCallback): () => void {
    this.orientationCallbacks.add(callback);
    return () => this.orientationCallbacks.delete(callback);
  }

  /**
   * Reads physical screen brightness on Android via Capacitor plugin.
   *
   * <p>Pre-integrated with dynamic import — funciona automaticamente assim
   * que o plugin estiver instalado:
   *
   * <pre>
   *   yarn add @capacitor-community/screen-brightness
   *   npx cap sync android
   * </pre>
   *
   * <p>Quando o pacote não está presente, o {@code await import()} lança
   * e capturamos retornando null — o painel cai pro fallback de overlay.
   */
  async getHardwareBrightness(): Promise<number | null> {
    try {
      if (
        !ScreenBrightness ||
        typeof ScreenBrightness.getBrightness !== 'function'
      ) {
        return null;
      }
      const result = await ScreenBrightness.getBrightness();
      const brightness = result?.brightness;
      if (typeof brightness !== 'number' || Number.isNaN(brightness)) {
        return null;
      }
      // Plugin retorna 0-1; converte pra 0-100
      return Math.max(0, Math.min(100, Math.round(brightness * 100)));
    } catch (e) {
      console.warn('[AndroidPlatformService] getHardwareBrightness failed:', e);
      return null;
    }
  }

  /**
   * Sets physical screen brightness on Android (0-100) via Capacitor plugin.
   * Throws 'unsupported' quando o plugin não está instalado — chamador deve
   * cair no fallback de overlay CSS.
   *
   * <p>Veja {@link #getHardwareBrightness} pra instruções de instalação.
   */
  async setHardwareBrightness(level: number): Promise<void> {
    const safeLevel = Math.max(0, Math.min(100, Math.round(level)));
    try {
      if (
        !ScreenBrightness ||
        typeof ScreenBrightness.setBrightness !== 'function'
      ) {
        const err = new Error(
          'Hardware brightness unsupported: @capacitor-community/screen-brightness missing',
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err as any).code = 'unsupported';
        throw err;
      }
      await ScreenBrightness.setBrightness({ brightness: safeLevel / 100 });
    } catch (e) {
      // Rethrow pra caller decidir o fallback
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((e as any)?.code !== 'unsupported') {
        console.warn(
          '[AndroidPlatformService] setHardwareBrightness failed:',
          e,
        );
      }
      throw e;
    }
  }

  // OTA Update methods

  /**
   * Install an OTA update bundle on Android.
   * @param bundlePath Local file path to the downloaded bundle (.apk file)
   * @returns Promise that resolves when installation is complete
   * @throws Error if installation fails or platform API is not available
   *
   * Note: This requires user permission to install from unknown sources.
   * The implementation uses Android Intent for APK installation.
   * You may need a Capacitor plugin for full functionality.
   */
  async installUpdate(bundlePath: string): Promise<void> {
    console.log('[AndroidPlatformService] Installing update:', bundlePath);

    if (!this.isNativeEnvironment()) {
      console.warn(
        '[AndroidPlatformService] Cannot install update in non-native mode',
      );
      throw new Error(
        'OTA installation is only supported on Android devices via Capacitor',
      );
    }

    try {
      // Capacitor é injetado dinamicamente na webview Android — sem types disponíveis
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Capacitor = (window as any).Capacitor;

      if (!Capacitor) {
        throw new Error('Capacitor is not available');
      }

      // KIOSK_HARDENING_PLAN Fase 1: tenta silent install via Device Owner
      // (zero clique humano). Se backend é stock_android, cai no fluxo
      // PackageInstaller padrão (precisa OK).
      try {
        const { tryNativeSilentInstall } = await import('../kioskMode');
        const fileUri = bundlePath.startsWith('file://')
          ? bundlePath
          : `file://${bundlePath}`;
        const silentResult = await tryNativeSilentInstall(fileUri);
        if (silentResult?.supported) {
          console.log(
            '[AndroidPlatformService] ✅ Silent install via',
            silentResult.backend,
            '— status:',
            silentResult.status,
          );
          return;
        }
        if (silentResult) {
          console.log(
            '[AndroidPlatformService] Silent install não suportado pelo backend',
            silentResult.backend,
            '— motivo:',
            silentResult.reason,
            '→ caindo pra PackageInstaller fallback',
          );
        }
      } catch (silentErr) {
        console.warn(
          '[AndroidPlatformService] Silent install error, falling back:',
          silentErr,
        );
      }

      // Fallback histórico: usa OTAPlugin (PackageInstaller intent — precisa OK humano)
      const OTAPlugin = Capacitor.Plugins?.OTA;
      if (OTAPlugin && OTAPlugin.installAPK) {
        console.log(
          '[AndroidPlatformService] Using OTA plugin to install APK (PackageInstaller path)',
        );
        return new Promise<void>((resolve, reject) => {
          OTAPlugin.installAPK({ path: bundlePath })
            .then(() => {
              console.log(
                '[AndroidPlatformService] Installation initiated successfully',
              );
              resolve();
            })
            .catch((error: Error) => {
              console.error(
                '[AndroidPlatformService] Installation failed:',
                error,
              );
              reject(
                new Error(
                  `Failed to install update: ${error.message || 'Unknown error'}`,
                ),
              );
            });
        });
      }

      // Fallback: Try to use Filesystem + Browser plugins to trigger download
      console.warn(
        '[AndroidPlatformService] OTA plugin not available. User must install manually.',
      );
      throw new Error(
        'OTA installation requires a custom Capacitor plugin. ' +
          'Please install the APK manually or implement an OTA plugin.',
      );
    } catch (error) {
      console.error('[AndroidPlatformService] Installation error:', error);
      throw new Error(
        `Installation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  /**
   * Restart após um update — caminho do OTA/rollback.
   *
   * <p><b>No Android isto recarrega a página, e tem que continuar assim.</b>
   * Matar o processo aqui derruba o diálogo do PackageInstaller que o
   * `installUpdate` acabou de abrir e recria o loop de install de 2026-05-28
   * (vide o comentário em `OtaService.installUpdate`, que por isso pula o
   * restart no Android). Quem chega aqui hoje é o `RollbackService`, logo
   * depois de reinstalar a versão anterior.</p>
   *
   * <p>Até a 1.0.452 este método tentava `Capacitor.Plugins.App.restart()`
   * antes de recarregar — um método que <b>nunca existiu</b> (o pacote
   * `@capacitor/app` não está instalado, e o plugin oficial só expõe
   * `exitApp()`). O `if` era sempre falso e o efeito real sempre foi o reload.
   * A 1.0.453 apenas remove o galho morto: o comportamento é o mesmo de
   * sempre, agora dito com honestidade.</p>
   *
   * <p>O restart de processo de verdade está em {@link restartProcess}, que é
   * de quem pediu por ele — o comando `app:restart` do admin.</p>
   */
  restartApp(): void {
    console.log('[AndroidPlatformService] Recarregando a página após update...');
    window.location.reload();
  }

  /**
   * Reinicia o PROCESSO (plugin nativo `OTA.restartApp` → `AppRestarter`).
   *
   * <p>Um reload não devolve memória nativa: medido em 04/10, 367 → 366 MB
   * numa TV, e 366 ainda 19 min depois. O que vaza vive fora da página; só um
   * processo novo alcança.</p>
   *
   * <p>Se o nativo não estiver no build (APK anterior à 1.0.453, WebView fora
   * do Capacitor) ou se recusar a sair porque não conseguiu agendar a volta,
   * cai no reload — melhor recarregar do que matar um app que não sabe como
   * voltar.</p>
   *
   * @since 1.0.453
   */
  restartProcess(): void {
    console.log('[AndroidPlatformService] Reiniciando o PROCESSO...');

    try {
      // Capacitor é injetado dinamicamente na webview Android — sem types disponíveis
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Capacitor = (window as any).Capacitor;

      if (!Capacitor?.Plugins?.OTA?.restartApp) {
        console.warn(
          '[AndroidPlatformService] OTA.restartApp() indisponível — recarregando a página',
        );
        window.location.reload();
        return;
      }

      Capacitor.Plugins.OTA.restartApp()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          // O caminho feliz não chega aqui: o processo morre antes. Se chegou
          // com supported=false, o nativo se recusou a sair — recarrega.
          if (res && res.supported === false) {
            console.warn(
              '[AndroidPlatformService] nativo recusou o restart:',
              res.reason,
            );
            window.location.reload();
          }
        })
        .catch((err: unknown) => {
          console.error('[AndroidPlatformService] OTA.restartApp falhou:', err);
          window.location.reload();
        });
    } catch (error) {
      console.error('[AndroidPlatformService] Failed to restart app:', error);
      // Fallback to reload
      window.location.reload();
    }
  }

  /**
   * Get temporary directory for OTA downloads.
   * On Android, this returns the cache directory path.
   * @returns Path to temp directory
   */
  getTempDirectory(): string {
    try {
      // Capacitor é injetado dinamicamente na webview Android — sem types disponíveis
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Capacitor = (window as any).Capacitor;

      if (Capacitor?.Plugins?.Filesystem) {
        // Use Capacitor Filesystem to get cache directory
        // This would typically be done asynchronously, but we return a known path
        return 'CACHE'; // Capacitor Filesystem directory constant
      }
    } catch (error) {
      console.warn(
        '[AndroidPlatformService] Could not get cache directory:',
        error,
      );
    }

    // Fallback
    console.log('[AndroidPlatformService] Using fallback temp path');
    return '/tmp';
  }

  /**
   * Salva blob no Filesystem do Capacitor (Directory.Cache) e retorna URI
   * absoluto resolvido (formato `file:///data/data/{app}/cache/{filename}`).
   *
   * <p><b>Bug histórico fixado em v1.0.84 (Renato + Claude):</b> Antes desse
   * método existir, o OtaService.saveBlobToFile caía no fallback "bundle saved
   * in memory only" — blob ficava em RAM, nada era escrito em disco. O
   * `bundlePath` passado pra `installUpdate` era literalmente a string
   * `"CACHE/ota_X.bundle"` (com o enum literal Capacitor como prefixo),
   * que no Java virava `new File("CACHE/...")` → path relativo → resolvia
   * pra `/ota_X.bundle` (root) → `ENOENT (No such file or directory)`.</p>
   *
   * <p><b>Fix:</b> escreve via {@code Filesystem.writeFile} com
   * {@code directory: Directory.Cache} (escreve em
   * `/data/data/{app}/cache/{filename}`). Pega URI absoluto via
   * {@code Filesystem.getUri()} e retorna pro caller usar como input
   * de install. Chunks de 5MB pra não OOM em APKs grandes.</p>
   *
   * @param blob Blob a salvar (típico: APK de 3-10MB baixado via axios)
   * @param requestedPath Path pedido (ex: "CACHE/ota_1.0.84_X.bundle").
   *        Apenas o filename final é usado — o diretório é sempre Cache.
   * @returns URI absoluto do arquivo salvo (formato file:///...)
   */
  async saveBlobToFile(blob: Blob, requestedPath: string): Promise<string> {
    // Extrai o filename do path solicitado (ignora prefixo de diretório).
    const filename = requestedPath.split('/').pop() || 'ota.bundle';

    console.log(
      `[AndroidPlatformService] saveBlobToFile: ${blob.size} bytes → Cache/${filename}`,
    );

    // Chunked base64 conversion — APKs podem ter 10MB+, base64 single-shot
    // estoura heap em devices antigos (Tab A7 tem 2GB RAM total).
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
    const totalChunks = Math.ceil(blob.size / CHUNK_SIZE);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, blob.size);
      const chunk = blob.slice(start, end);
      const base64 = await this.blobChunkToBase64(chunk);

      if (i === 0) {
        // Primeiro chunk: writeFile (cria/trunca arquivo existente)
        await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        });
      } else {
        // Chunks seguintes: appendFile
        await Filesystem.appendFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        });
      }
    }

    // Resolve URI absoluto (necessário pra o OTAPlugin nativo achar o arquivo).
    const { uri } = await Filesystem.getUri({
      path: filename,
      directory: Directory.Cache,
    });

    console.log(`[AndroidPlatformService] saveBlobToFile OK: ${uri}`);
    return uri;
  }

  /**
   * Lista os arquivos do cache do app (`/data/data/{app}/cache/`).
   *
   * Existe por causa de um vazamento real: `OtaService` grava cada download
   * como `ota_<versao>_<Date.now()>.bundle` e NINGUÉM apagava. O `Date.now()`
   * no nome garante arquivo novo a cada tentativa, então nem re-download da
   * mesma versão reaproveitava. Medido no Tab A7 em 2026-09-20: **34
   * arquivos, 123 MB**, o mais antigo da v1.0.194 — com duplicatas das
   * versões 1.0.403, 1.0.406, 1.0.407 e 1.0.427. Em 6000 tablets, crescendo
   * a cada release.
   */
  async listTempFiles(): Promise<string[]> {
    try {
      const { files } = await Filesystem.readdir({
        path: '',
        directory: Directory.Cache,
      });
      return files.map((f) => (typeof f === 'string' ? f : f.name));
    } catch (err) {
      console.warn('[AndroidPlatformService] readdir do cache falhou:', err);
      return [];
    }
  }

  /** Apaga um arquivo do cache do app. Vide {@link listTempFiles}. */
  async deleteTempFile(name: string): Promise<void> {
    await Filesystem.deleteFile({ path: name, directory: Directory.Cache });
  }

  /**
   * Helper: converte um chunk de Blob em base64 string via FileReader.
   * Necessário porque Capacitor Filesystem só aceita base64 como input.
   */
  private blobChunkToBase64(chunk: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('FileReader retornou non-string'));
          return;
        }
        // dataURL é "data:<mime>;base64,XXX" — extrai só o XXX
        const commaIdx = result.indexOf(',');
        resolve(commaIdx >= 0 ? result.substring(commaIdx + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(chunk);
    });
  }

  // Fullscreen methods (Android WebView supports standard Fullscreen API)

  /**
   * Android WebView supports fullscreen toggle via standard Fullscreen API.
   */
  supportsFullscreen(): boolean {
    return !!(
      document.fullscreenEnabled ||
      (document as DocumentWithVendorFullscreen).webkitFullscreenEnabled ||
      (document as DocumentWithVendorFullscreen).mozFullScreenEnabled ||
      (document as DocumentWithVendorFullscreen).msFullscreenEnabled
    );
  }

  /**
   * Checks if currently in fullscreen mode.
   */
  isFullscreen(): boolean {
    return !!(
      document.fullscreenElement ||
      (document as DocumentWithVendorFullscreen).webkitFullscreenElement ||
      (document as DocumentWithVendorFullscreen).mozFullScreenElement ||
      (document as DocumentWithVendorFullscreen).msFullscreenElement
    );
  }

  /**
   * Enters fullscreen mode for the specified element.
   * @param element Element to fullscreen (defaults to document.documentElement)
   */
  async enterFullscreen(element?: HTMLElement): Promise<void> {
    const el = element || document.documentElement;

    try {
      const elVendor = el as ElementWithVendorFullscreen;
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (elVendor.webkitRequestFullscreen) {
        await elVendor.webkitRequestFullscreen();
      } else if (elVendor.mozRequestFullScreen) {
        await elVendor.mozRequestFullScreen();
      } else if (elVendor.msRequestFullscreen) {
        await elVendor.msRequestFullscreen();
      }
      console.log('[AndroidPlatformService] Entered fullscreen');
    } catch (error) {
      console.error(
        '[AndroidPlatformService] Failed to enter fullscreen:',
        error,
      );
      throw error;
    }
  }

  /**
   * Exits fullscreen mode.
   */
  async exitFullscreen(): Promise<void> {
    try {
      const docVendor = document as DocumentWithVendorFullscreen;
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (docVendor.webkitExitFullscreen) {
        await docVendor.webkitExitFullscreen();
      } else if (docVendor.mozCancelFullScreen) {
        await docVendor.mozCancelFullScreen();
      } else if (docVendor.msExitFullscreen) {
        await docVendor.msExitFullscreen();
      }
      console.log('[AndroidPlatformService] Exited fullscreen');
    } catch (error) {
      console.error(
        '[AndroidPlatformService] Failed to exit fullscreen:',
        error,
      );
      throw error;
    }
  }

  /**
   * Registers a callback for fullscreen change events.
   * Returns an unsubscribe function.
   */
  onFullscreenChange(callback: (isFullscreen: boolean) => void): () => void {
    this.fullscreenCallbacks.add(callback);

    const handleFullscreenChange = (): void => {
      const isFs = this.isFullscreen();
      this.fullscreenCallbacks.forEach((cb) => {
        try {
          cb(isFs);
        } catch (e) {
          console.error(
            '[AndroidPlatformService] Error in fullscreen callback:',
            e,
          );
        }
      });
    };

    // Add listeners for all vendor prefixes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      this.fullscreenCallbacks.delete(callback);
      // Only remove listeners if no more callbacks
      if (this.fullscreenCallbacks.size === 0) {
        document.removeEventListener(
          'fullscreenchange',
          handleFullscreenChange,
        );
        document.removeEventListener(
          'webkitfullscreenchange',
          handleFullscreenChange,
        );
        document.removeEventListener(
          'mozfullscreenchange',
          handleFullscreenChange,
        );
        document.removeEventListener(
          'MSFullscreenChange',
          handleFullscreenChange,
        );
      }
    };
  }

  // Screen Wake Lock (V058) — no-op no Android: a tela já fica acesa via
  // WAKE_LOCK nativo + ScreenOffReceiver (Device Owner). A flag
  // keepScreenAwake é de browser; aqui é coberto nativamente.
  supportsWakeLock(): boolean {
    return false;
  }

  async requestScreenWakeLock(): Promise<void> {
    // no-op (tela acesa nativa via ScreenOffReceiver/WAKE_LOCK)
  }

  async releaseScreenWakeLock(): Promise<void> {
    // no-op
  }
}
